import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowRight, CheckCircle, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useTrackEvent } from './AnalyticsProvider';
import DemoVideoModal from './DemoVideoModal';
import MarketingLayout from './MarketingLayout';
import PageHero from './PageHero';
import Breadcrumb from './Breadcrumb';
import { PLANS, PRICING_CONFIG } from '../config/pricing';

export interface ProblemCard {
  title: string;
  description: string;
  icon: LucideIcon;
  iconBgClass: string;
  iconColorClass: string;
}

export interface FeatureCard {
  title: string;
  description: string;
  icon: LucideIcon;
  gradientFrom: string;
  gradientTo: string;
  iconColorClass: string;
}

export interface KeywordLandingPageProps {
  headline: string;
  subheadline: string;
  bullets: string[];
  badgeText?: string;
  showSecondaryCta?: boolean;
  secondaryCtaLabel?: string;
  problemTitle: ReactNode;
  problemSubtitle: string;
  problemCards: ProblemCard[];
  solutionTitle: ReactNode;
  solutionSubtitle: string;
  solutionSteps: { title: string; description: string }[];
  featuresTitle: string;
  features: FeatureCard[];
  finalCtaTitle: string;
  finalCtaSubtitle: string;
  finalCtaButtonText: string;
  analyticsCategory: string;
  breadcrumbLabel: string;
}

export default function KeywordLandingPage(props: KeywordLandingPageProps) {
  const trackEvent = useTrackEvent();
  const founder = PRICING_CONFIG[PLANS.FOUNDER];
  const pro = PRICING_CONFIG[PLANS.PRO];
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const handleSignUpClick = (location: string) => {
    trackEvent({ action: 'sign_up_click', category: props.analyticsCategory, label: location });
  };
  const handleSignInClick = () => {
    trackEvent({ action: 'sign_in_click', category: props.analyticsCategory, label: 'header_cta' });
  };
  const handleDemoClick = () => {
    trackEvent({ action: 'demo_click', category: props.analyticsCategory, label: 'hero_demo' });
    setIsDemoModalOpen(true);
  };

  return (
    <MarketingLayout
      onSignInClick={handleSignInClick}
      onSignUpClick={() => handleSignUpClick('header')}
    >
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="public-container py-3">
          <Breadcrumb items={[{ label: props.breadcrumbLabel }]} />
        </div>
      </div>
      <PageHero
        badge={props.badgeText ? <span>{props.badgeText}</span> : undefined}
        title={props.headline}
        subtitle={props.subheadline}
      >
        <Link
          to="/sign-up"
          onClick={() => handleSignUpClick('hero_primary')}
          className="public-button public-button--primary"
        >
          Start my strategic plan <ArrowRight className="h-5 w-5" />
        </Link>
        {props.showSecondaryCta && (
          <button onClick={handleDemoClick} className="public-button public-button--secondary">
            <Play className="h-5 w-5" /> {props.secondaryCtaLabel || 'Watch the demo'}
          </button>
        )}
      </PageHero>
      <ul className="public-container flex flex-wrap justify-center gap-x-8 gap-y-3 pb-10 text-sm text-gray-600">
        {props.bullets.map((bullet) => (
          <li key={bullet} className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-blue-600 shrink-0" />
            {bullet}
          </li>
        ))}
      </ul>

      <section className="public-section">
        <div className="public-container">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <p className="public-eyebrow">Make the next move clearer</p>
            <h2 className="mb-5">{props.problemTitle}</h2>
            <p className="text-xl">{props.problemSubtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {props.problemCards.map((card) => {
              const Icon = card.icon;
              return (
                <article key={card.title} className="public-card p-7">
                  <div
                    className={`w-12 h-12 ${card.iconBgClass} rounded-xl flex items-center justify-center mb-5`}
                  >
                    <Icon className={`h-6 w-6 ${card.iconColorClass}`} />
                  </div>
                  <h3 className="mb-3">{card.title}</h3>
                  <p className="text-gray-600">{card.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="public-section">
        <div className="public-container">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <p className="public-eyebrow">A connected way forward</p>
            <h2 className="mb-5">{props.solutionTitle}</h2>
            <p className="text-xl">{props.solutionSubtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {props.solutionSteps.map((step, index) => (
              <article key={step.title} className="public-card public-card--white p-7">
                <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 font-bold mb-5">
                  {index + 1}
                </span>
                <h3 className="mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="public-section">
        <div className="public-container">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2>{props.featuresTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {props.features.map((feature) => {
              const Icon = feature.icon;
              return (
                <article key={feature.title} className="public-card p-7">
                  <Icon className={`h-8 w-8 mb-5 ${feature.iconColorClass}`} />
                  <h3 className="mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="public-section">
        <div className="public-container">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <p className="public-eyebrow">Choose your starting point</p>
            <h2 className="mb-5">Build the plan. Activate your team.</h2>
            <p className="text-xl">
              Start with Founder for strategic planning. Add Pro Activation for team execution and
              AI assistance.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <article className="public-card public-card--white p-8">
              <p className="public-eyebrow">1 company · 1 user</p>
              <h3 className="mb-4">Founder</h3>
              <div className="mb-5">
                <span className="text-4xl font-bold">
                  ${founder.price.launch?.amount ?? founder.price.standard.amount}
                </span>
                <span className="text-gray-600">
                  {' '}
                  USD {founder.price.launch ? 'for the first year' : '/ year'}
                </span>
                <p className="text-sm text-gray-600 mt-2">
                  {founder.price.launch
                    ? `Renews at $${founder.price.standard.amount}/year.`
                    : 'Billed annually.'}
                </p>
              </div>
              <p className="text-gray-600 mb-5">
                Define your direction, assess your business and develop a clear strategic plan.
              </p>
              <ul className="space-y-3 mb-7">
                {[
                  'Vision, mission and values',
                  'Strategic objectives',
                  'SWOT, PESTEL and Five Forces',
                  'Strategy formulation and plan summary',
                  'Visual organization chart',
                ].map((item) => (
                  <li key={item} className="flex gap-3 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/sign-up"
                onClick={() => handleSignUpClick('pricing_founder')}
                className="public-button public-button--primary w-full"
              >
                Start with Founder <ArrowRight className="h-5 w-5" />
              </Link>
            </article>
            <article className="public-card p-8">
              <p className="public-eyebrow">Requires Founder · Minimum 5 users</p>
              <h3 className="mb-4">Pro Activation</h3>
              <div className="mb-5">
                <span className="text-4xl font-bold">
                  ${pro.price.launch?.amount ?? pro.price.standard.amount}
                </span>
                <span className="text-gray-600"> USD / user / month</span>
                <p className="text-sm text-gray-600 mt-2">
                  {pro.price.launch
                    ? `First-year launch price. Renews at $${pro.price.standard.amount}/user/month.`
                    : 'Billed monthly.'}
                </p>
              </div>
              <p className="text-gray-600 mb-5">
                Connect your leadership team to the work of turning strategy into action.
              </p>
              <ul className="space-y-3 mb-7">
                {[
                  'AI-assisted strategy and planning',
                  'Execution Hub with projects and tasks',
                  'Task delegation and accountability',
                  'Objective tracking and execution progress',
                  'Role-based visibility',
                  'Priority support',
                ].map((item) => (
                  <li key={item} className="flex gap-3 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/sign-up"
                onClick={() => handleSignUpClick('pricing_pro')}
                className="public-button public-button--primary w-full"
              >
                Activate Pro <ArrowRight className="h-5 w-5" />
              </Link>
            </article>
          </div>
          <p className="text-center text-gray-600 mt-7 text-sm">
            Launch pricing applies to the first year only. Renewal uses the standard rates shown
            above.
          </p>
        </div>
      </section>

      <section className="public-cta">
        <div className="public-container text-center">
          <h2 className="mb-4">{props.finalCtaTitle}</h2>
          <p className="max-w-2xl mx-auto mb-7">{props.finalCtaSubtitle}</p>
          <Link
            to="/sign-up"
            onClick={() => handleSignUpClick('final_cta')}
            className="public-button public-button--primary"
          >
            {props.finalCtaButtonText} <ArrowRight className="h-5 w-5" />
          </Link>
          <p className="mt-5 text-sm">
            <Link to="/pricing" className="text-blue-600">
              Explore plans and pricing
            </Link>
          </p>
        </div>
      </section>
      <DemoVideoModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} />
    </MarketingLayout>
  );
}
